import React, { useState, useEffect, useCallback } from 'react';
import { Box, Avatar, Typography, Switch, Pagination, Stack, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import api from '../api/axios';
import { PageHeader, Toolbar, DataTable, StatusBadge, useFeedback } from '../components/ui';
import { color } from '../theme/tokens';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';

const Users = () => {
  const feedback = useFeedback();
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [inputVal, setInputVal]   = useState('');
  const [search, setSearch]       = useState('');
  const [sort, setSort]           = useState('newest');
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]         = useState(0);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/users', { 
        params: { 
          search: search || undefined, 
          sort: sort || undefined,
          page, 
          pagesize: 20 
        } 
      });
      setUsers(res.data.data.users);
      setTotalPages(res.data.data.pagination.totalPages);
      setTotal(res.data.data.pagination.total);
    } catch {
      feedback.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, sort, page, feedback]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { const t = setTimeout(() => { setSearch(inputVal); setPage(1); }, 400); return () => clearTimeout(t); }, [inputVal]);

  const handleToggle = async (srno) => {
    try {
      await api.patch(`/api/admin/users/${srno}/toggle`);
      fetchUsers();
    } catch {
      feedback.error('Failed to update user status');
    }
  };

  const columns = [
    {
      key: 'NAME', label: 'User', width: 240,
      render: (v, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={row.PROFILE_PIC_URL || undefined}
            sx={{ width: 32, height: 32, bgcolor: color.primary[500], color: '#FFFFFF', fontSize: '0.8rem' }}
          >
            {row.NAME?.[0]?.toUpperCase()}
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.NAME || '—'}</Typography>
        </Box>
      ),
    },
    { key: 'EMAIL', label: 'Email' },
    { key: 'MOBILENO', label: 'Mobile' },
    {
      key: 'CITY', label: 'City / State',
      render: (v, row) => [row.CITY, row.STATE].filter(Boolean).join(', ') || '—',
    },
    { key: 'ENTRY_TIME', label: 'Joined', render: (v) => fmtDate(v) },
    {
      key: 'AUTH_PROVIDER', label: 'Provider',
      render: (v) => <StatusBadge label={v === 'google' ? 'Google' : 'Email'} tone={v === 'google' ? 'info' : 'neutral'} />,
    },
    {
      key: 'ACTIVE_STATUS', label: 'Status',
      render: (v) => <StatusBadge label={v === 1 ? 'Active' : 'Disabled'} tone={v === 1 ? 'success' : 'neutral'} />,
    },
    {
      key: 'SRNO', label: 'Active', align: 'right', width: 88,
      render: (v, row) => (
        <Switch size="small" checked={row.ACTIVE_STATUS === 1} onChange={() => handleToggle(row.SRNO)} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Customers"
        title="Users"
        subtitle={`${total} registered customers`}
        actions={
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={fetchUsers} disabled={loading} aria-label="Refresh">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      <Toolbar
        search={inputVal}
        onSearch={setInputVal}
        searchPlaceholder="Search by name, email or mobile..."
        onRefresh={fetchUsers}
        refreshing={loading}
        sx={{ mb: 2.5 }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sort}
            label="Sort By"
            onChange={e => { setSort(e.target.value); setPage(1); }}
          >
            <MenuItem value="newest">Newest Joined</MenuItem>
            <MenuItem value="oldest">Oldest Joined</MenuItem>
            <MenuItem value="name-asc">Name (A-Z)</MenuItem>
            <MenuItem value="name-desc">Name (Z-A)</MenuItem>
          </Select>
        </FormControl>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={users}
        rowKey="SRNO"
        loading={loading}
        minWidth={1000}
        emptyTitle="No users found"
        emptyDescription="No customers match your current filters."
        getRowSx={(row) => (row.ACTIVE_STATUS === 0 ? { opacity: 0.55 } : {})}
      />

      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
        </Stack>
      )}
    </Box>
  );
};

export default Users;
